import React, { useCallback, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { handleKeyEvents } from 'utils/handlers';
import { useAppSelector } from 'utils/hooks';
import activeEditor from 'domains/active_editor';

import { ShufflerContext } from './Shuffler';

import ShufflerProduct from './ShufflerProduct';
import type { ShufflerContextType } from './Shuffler';
import type { OptionAPIData } from 'domains/option_groups';

function navigatePrevious(el: HTMLElement) {
  const parent = el.parentElement;
  if (!parent) return;

  const v = Array.from(parent.children) as HTMLElement[];
  const i = v.indexOf(el);
  const n = i === 0 ? v.length - 1 : i - 1;
  v[n].focus();
}

function navigateNext(el: HTMLElement) {
  const parent = el.parentElement;
  if (!parent) return;

  const v = Array.from(parent.children) as HTMLElement[];
  const i = v.indexOf(el);
  const n = i < v.length - 1 ? i + 1 : 0;
  v[n].focus();
}

function navigateAbove(el: HTMLElement) {
  const parent = el.parentElement;
  if (!parent) return;

  const v = Array.from(parent.children).reverse() as HTMLElement[];

  const { x: elemLeft, y: elemTop } = el.getBoundingClientRect();
  // if user arrows up from the top row, focus on the row title element
  const n =
    v.find((o) => {
      const { x: optionLeft, y: optionTop } = o.getBoundingClientRect();
      return o !== el && optionLeft === elemLeft && optionTop < elemTop;
    }) || el.parentElement.previousElementSibling;

  if (n instanceof HTMLElement) {
    n.focus();
  }
}

function navigateBelow(el: HTMLElement) {
  const parent = el.parentElement;
  if (!parent) return;

  const v = Array.from(parent.children) as HTMLElement[];

  const { x: elemLeft, y: elemTop } = el.getBoundingClientRect();
  const n =
    v.find((o) => {
      const { x: optionLeft, y: optionTop } = o.getBoundingClientRect();
      return o !== el && optionLeft === elemLeft && optionTop > elemTop;
    }) || el;
  n.focus();
}

function handleNavigation({ key, currentTarget }: React.KeyboardEvent) {
  if (currentTarget instanceof HTMLElement) {
    switch (key) {
      case 'ArrowRight':
        return navigateNext(currentTarget);
      case 'ArrowLeft':
        return navigatePrevious(currentTarget);
      case 'ArrowUp':
        return navigateAbove(currentTarget);
      case 'ArrowDown':
        return navigateBelow(currentTarget);
      default:
        return false;
    }
  }
}

function RelatedProductButton(props: { relatedProduct: ShufflerProduct }) {
  const {
    relatedProduct: { id, uid, subcatalog, name, imageUrl, size, /*format,*/ orientation },
  } = props;
  const { product, setProduct, itemClick } = useContext(ShufflerContext) as ShufflerContextType;

  const handleRelatedProductButtonClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      itemClick(e);
    },
    [itemClick]
  );

  return (
    <div
      key={uid}
      role="button"
      tabIndex={0}
      className={`related ${uid === product?.uid ? 'active' : ''}`}
      data-id={id}
      data-catalog={subcatalog}
      onClick={handleRelatedProductButtonClick}
      onKeyDown={handleKeyEvents({
        open: itemClick,
        navigate: handleNavigation,
        close: () => setProduct(undefined),
      })}
    >
      <img
        alt={name}
        src={imageUrl}
        // title={`${name}\n${size} ${format}\n${orientation}`}
        title={`${name}\n${size} \n${orientation}`}
      />
    </div>
  );
}

type OptionButtonProps = {
  option?: OptionAPIData;
  onClick: (e: React.MouseEvent | React.KeyboardEvent) => void;
  isSelected: boolean;
};

function OptionButton(props: OptionButtonProps) {
  const { option, onClick, isSelected } = props;
  const { setProduct, currentOptionGroup } = useContext(ShufflerContext) as ShufflerContextType;

  if (!option) return null;

  const { id: optionId, uid, name: optionName, image_url: imageUrl } = option;

  return (
    <div
      key={optionId}
      role="button"
      tabIndex={0}
      className={`option ${isSelected ? 'active selected' : ''}`}
      data-id={optionId}
      data-uid={uid}
      data-groupname={currentOptionGroup?.name}
      onClick={onClick}
      onKeyDown={handleKeyEvents({
        open: onClick,
        navigate: handleNavigation,
        close: () => setProduct(undefined),
      })}
      title={optionName}
    >
      <img src={imageUrl} alt={optionName} />
      <div className="option_text">{optionName}</div>
    </div>
  );
}

type StandardProductDetailProps = {
  onClick: (e: React.MouseEvent | React.KeyboardEvent) => void;
};

function StandardProductDetail(props: StandardProductDetailProps) {
  const { onClick } = props;

  const {
    staticFilterData,
    product,
    catalogOptionGroups,
    productOptionGroups,
    currentOptionGroup,
    relatedProducts,
    optionGroupClick,
    setProduct,
    showRelatedProducts,
  } = useContext(ShufflerContext) as ShufflerContextType;

  const activeOptions = useAppSelector(activeEditor.selectors.selectedOptions);

  function getSelectedOption(groupId: number) {
    return activeOptions.find((o) => o.parent_id === groupId);
  }

  const excludeDetails = ['Format', 'Orientation'];

  if (!product) return null;

  return (
    <React.Fragment>
      <div className="left-side">
        <div className="product_detail">
          <img
            id="product_detail_image"
            src={product.imageUrl}
            alt={product.name}
            className="fadein"
          />
        </div>

        {showRelatedProducts && (
          <div className="content_box">
            <div className="related_products">
              {relatedProducts.map(
                (other, i, others) =>
                  others.length > 1 && (
                    <RelatedProductButton key={other.id} relatedProduct={other} />
                  )
              )}
            </div>
          </div>
        )}
      </div>

      <div className="right-side">
        <div className="product_specifications">
          <div className="product_subcatalog">{product.subcatalog}</div>
          {staticFilterData?.detailFilters
            .filter((t) => excludeDetails.indexOf(t) === -1)
            .map((df) => (
              <div
                key={df}
                title={product[df.toLowerCase() as keyof ShufflerProduct]?.toString()}
                className="product_spec"
              >
                <span className="spec_name">{df}:</span>{' '}
                <span className="spec_value">
                  {product[df.toLowerCase() as keyof ShufflerProduct]?.toString()}
                </span>
              </div>
            ))}
          <div className="product_spec">
            <span className="spec_name">Number of images:</span>{' '}
            <span className="spec_value">{product.metaData.numphotos}</span>
          </div>
        </div>

        <div className="content_box">
          {catalogOptionGroups.length > 0 && (
            <div className="option_groups_list">
              <div className="option_groups_button_container">
                {productOptionGroups.length === 0 && '...Loading product options'}
                {productOptionGroups.length > 0 &&
                  productOptionGroups.map(
                    ({ id: groupId, name: groupName, options: groupOptions }) => (
                      <div key={groupId} className="options_collapsible">
                        <div
                          role="link"
                          tabIndex={0}
                          className="options_collapsible_title"
                          data-groupname={groupName}
                          onClick={optionGroupClick}
                          // onKeyDown={handleKeyEvents(
                          //   {
                          //     open: optionGroupClick,
                          //     alwaysOpen: (e) => optionGroupClick(e, true),
                          //     alwaysClose: (e) => optionGroupClick(e, false),
                          //     close: () => setProduct(undefined),
                          //   }
                          // )}
                        >
                          <div className="option-title">
                            <span className="option-title-name">{groupName}</span>
                            {(activeOptions.find((o) => o.parent_id === groupId) ||
                              groupOptions.length) && (
                              <div className="option-title-details">
                                <img
                                  className="selected-option-thumb"
                                  src={getSelectedOption(groupId)?.image_url}
                                  alt={`Current selection : ${
                                    activeOptions.find((o) => o.parent_id === groupId)?.id ||
                                    groupOptions[0]
                                  }`}
                                />
                                <div>
                                  <strong>{getSelectedOption(groupId)?.name || ''}</strong>
                                  <span>{getSelectedOption(groupId)?.service.price || ''}</span>
                                </div>
                              </div>
                            )}
                          </div>
                          <FontAwesomeIcon
                            icon={
                              currentOptionGroup && currentOptionGroup.name === groupName
                                ? 'chevron-up'
                                : 'chevron-down'
                            }
                          />
                        </div>
                        {currentOptionGroup && currentOptionGroup.name === groupName && (
                          <div className="options_collapsible_content current_options">
                            {currentOptionGroup.options.map((curOpt) => (
                              <OptionButton
                                key={curOpt.id}
                                data-id={curOpt.id}
                                option={curOpt}
                                onClick={onClick}
                                isSelected={
                                  curOpt.id ===
                                  activeOptions.find(
                                    ({ parent_id: activeGroupId }) => activeGroupId === groupId
                                  )?.id
                                }
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  )}
              </div>
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
}

export default StandardProductDetail;
